import { Link } from "react-router-dom";
import { ArrowUpRight, Target, Lightning, ShieldCheck, Users } from "@phosphor-icons/react";

const PILLARS = [
  {
    icon: Target,
    title: "Curated, not scraped",
    body: "Every platform on this board is checked by hand. If a network stops paying or closes signups, it comes off the list.",
  },
  {
    icon: Lightning,
    title: "Updated constantly",
    body: "New expert networks, one-time studies, voice projects and micro-task platforms are added as soon as they open their doors.",
  },
  {
    icon: ShieldCheck,
    title: "No paywalls to apply",
    body: "Applying through this board is always free. Companies pay to feature a listing — job seekers never do.",
  },
  {
    icon: Users,
    title: "Built for real people",
    body: "Students, translators, nurses, developers, lawyers and stay-at-home parents all train AI. The board reflects that range.",
  },
];

export default function AboutPage() {
  return (
    <main className="min-h-screen bg-[#05050A]" data-testid="about-page">
      <section className="relative pt-28 pb-16 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="tech-hero-grid" />
        <div className="relative max-w-4xl mx-auto">
          <span className="tag px-3 py-1 bg-[#D9F839]/10 text-[#D9F839] rounded-full mb-5 inline-block" data-testid="about-eyebrow">
            About theotautomations.com
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white mb-6" style={{ fontFamily: "Unbounded, sans-serif" }} data-testid="about-title">
            We track every place that pays people to <span className="text-[#D9F839]">train AI</span>.
          </h1>
          <p className="text-base md:text-lg text-[#B8B9C3] leading-relaxed mb-6" data-testid="about-intro">
            theotautomations.com is an independent job board for the AI training economy. Expert networks, research
            studies, voice data projects and micro-task platforms all hire remotely, pay in dollars and rarely advertise
            in one place. We put them in one place.
          </p>
          <Link to="/?focus=jobs" className="cta-button inline-flex items-center gap-2 rounded-full px-6 py-3" data-testid="about-browse-jobs-button">
            Browse the job board <ArrowUpRight size={16} weight="bold" />
          </Link>
        </div>
      </section>

      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto grid gap-4 md:grid-cols-2">
          {PILLARS.map((pillar) => (
            <div className="panel-card p-6" key={pillar.title} data-testid={`about-pillar-${pillar.title.split(" ")[0].toLowerCase()}`}>
              <pillar.icon size={24} className="text-[#D9F839] mb-4" />
              <h2 className="text-base md:text-lg text-white mb-2">{pillar.title}</h2>
              <p className="text-sm text-[#A1A1AA] leading-relaxed">{pillar.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto panel-card p-8">
          <h2 className="text-base md:text-lg text-white mb-4" style={{ fontFamily: "Unbounded, sans-serif" }} data-testid="about-story-heading">
            Why we built it
          </h2>
          <div className="space-y-4 text-sm text-[#B8B9C3] leading-relaxed" data-testid="about-story-body">
            <p>
              AI models are trained by people — subject-matter experts writing reference answers, reviewers grading model
              output, native speakers recording audio, annotators labelling images. The work is remote, flexible and
              genuinely well paid, but the listings are scattered across dozens of private talent networks.
            </p>
            <p>
              We got tired of hunting through Discord servers and Reddit threads for the same handful of links, so we
              started keeping a list. That list turned into this board: searchable listings, platform directories by
              category, and guides that explain what each type of work actually involves before you apply.
            </p>
            <p>
              The board is funded by companies paying to feature their listings and by newsletter sponsorships. Some
              platform links are partner links, which never changes whether a platform gets listed or how it is described.
            </p>
          </div>
        </div>
      </section>

      <section className="py-12 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto panel-card p-8 flex flex-wrap items-center justify-between gap-6">
          <div>
            <h2 className="text-base md:text-lg text-white mb-2" style={{ fontFamily: "Unbounded, sans-serif" }}>Hiring AI trainers?</h2>
            <p className="text-sm text-[#A1A1AA]">Feature your roles in front of an audience that already does this work.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/post-a-job" className="cta-button inline-flex items-center gap-2 rounded-full px-6 py-3" data-testid="about-post-job-button">
              Post a job <ArrowUpRight size={16} weight="bold" />
            </Link>
            <Link to="/contact" className="secondary-button inline-flex items-center gap-2 rounded-full px-6 py-3" data-testid="about-contact-button">
              Contact us
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
